import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Wallet, Globe, ArrowDownUp } from "lucide-react";
import { toast } from "sonner";
import { isSupportedCrypto } from "@/lib/supported-symbols";

interface BalanceResult {
  network: string;
  address: string;
  nativeBalance: number;
  nativeCurrency: string;
  tokens: Array<{ symbol: string; balance: number }>;
}

interface AggregatedBalance {
  symbol: string;
  totalBalance: number;
  addressCount: number;
}

interface ProfitSummary {
  address: string;
  recipientId: string;
  recipientName: string;
  internalUsdtBalance: number;
  pendingUsd: number;
  sentUsd: number;
  lastBatchStatus?: string;
  recentTransfers?: Array<{ id: string; amount: number; transaction_number: string | null; created_at: string }>;
}

const CHAIN_MAP: Record<string, string> = {
  "Polygon (POS)": "0x89",
};

const NATIVE_SYMBOLS: Record<string, string> = {
  "Polygon (POS)": "POL",
};

const AdminBlockchainBalances = () => {
  const [loading, setLoading] = useState(false);
  const [aggregated, setAggregated] = useState<AggregatedBalance[]>([]);
  const [networkDetails, setNetworkDetails] = useState<Record<string, BalanceResult[]>>({});
  const [profitSummary, setProfitSummary] = useState<ProfitSummary | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchProfitSummary = async () => {
    const [{ data: settings }, { data: fees }, { data: batches }] = await Promise.all([
      supabase.from("platform_settings").select("setting_key, setting_value").in("setting_key", ["admin_wallet_address", "admin_wallet_network", "profit_recipient_numeric_id"]),
      supabase.from("platform_fee_accruals").select("fee_usd, status"),
      supabase.from("platform_fee_batches").select("status, created_at").order("created_at", { ascending: false }).limit(1),
    ]);

    const settingsMap = Object.fromEntries((settings || []).map((s) => [s.setting_key, s.setting_value]));
    const recipientId = settingsMap.profit_recipient_numeric_id || "5074064265790661";
    const { data: admin2 } = await supabase
      .from("admin_wallet_identities")
      .select("user_id, display_name, numeric_id")
      .eq("numeric_id", recipientId)
      .maybeSingle();
    const [{ data: platformTransfers }, { data: adminWallets }] = await Promise.all([
      admin2?.user_id
        ? supabase.from("internal_transfers").select("id, amount, transaction_number, created_at").eq("recipient_id", admin2.user_id).eq("source_type", "platform").order("created_at", { ascending: false }).limit(5)
        : Promise.resolve({ data: [] as any[] }),
      admin2?.user_id
        ? supabase.from("wallets").select("crypto_symbol, balance").eq("user_id", admin2.user_id).eq("crypto_symbol", "USDT")
        : Promise.resolve({ data: [] as any[] }),
    ]);
    setProfitSummary({
      address: settingsMap.admin_wallet_address || "غير محدد",
      recipientId,
      recipientName: admin2?.display_name || "المسؤول 2",
      internalUsdtBalance: Number(adminWallets?.[0]?.balance || 0),
      pendingUsd: (fees || []).filter((f) => f.status === "pending").reduce((sum, f) => sum + Number(f.fee_usd || 0), 0),
      sentUsd: (fees || []).filter((f) => f.status === "sent").reduce((sum, f) => sum + Number(f.fee_usd || 0), 0),
      lastBatchStatus: batches?.[0]?.status,
      recentTransfers: platformTransfers || [],
    });
  };

  const fetchBalances = async () => {
    setLoading(true);
    try {
      await fetchProfitSummary();
      // Get all unique addresses grouped by network
      const { data: addresses, error } = await supabase
        .from("user_crypto_addresses")
        .select("network, address")
        .eq("network", "Polygon (POS)");

      if (error) throw error;

      // Always include the central platform wallet (Index 0 of MNEMONIC_EVM)
      // so admin can see real operational balances even when it has never
      // been assigned to a user in user_crypto_addresses.
      let centralAddress: string | null = null;
      try {
        const { data: central } = await supabase.functions.invoke("get-central-address");
        if (central?.address) centralAddress = central.address;
      } catch (e) { console.error("central address fetch failed", e); }

      const combined = [...(addresses || [])];
      if (centralAddress) {
        combined.push({ network: "Polygon (POS)", address: centralAddress });
      }

      if (combined.length === 0) {
        toast.info("لا توجد عناوين مسجلة");
        setLoading(false);
        return;
      }

      // Deduplicate addresses per network
      const uniqueByNetwork: Record<string, Set<string>> = {};
      for (const a of combined) {
        if (!uniqueByNetwork[a.network]) uniqueByNetwork[a.network] = new Set();
        uniqueByNetwork[a.network].add(a.address);
      }

      const allResults: BalanceResult[] = [];
      const balanceMap: Record<string, { total: number; count: number }> = {};

      // Check balances for each unique address (batch by network)
      for (const [network, addrs] of Object.entries(uniqueByNetwork)) {
        const chainId = CHAIN_MAP[network];
        if (!chainId) continue;

        for (const address of addrs) {
          try {
            const { data, error: fnErr } = await supabase.functions.invoke("check-balance", {
              body: { address, chain: chainId },
            });

            if (fnErr || !data) continue;

            const result: BalanceResult = {
              network,
              address,
              nativeBalance: data.nativeBalance || 0,
              nativeCurrency: NATIVE_SYMBOLS[network] || data.nativeCurrency,
              tokens: data.tokens || [],
            };
            allResults.push(result);

            // Aggregate native
            if (result.nativeBalance > 0) {
              const sym = result.nativeCurrency;
              if (!balanceMap[sym]) balanceMap[sym] = { total: 0, count: 0 };
              balanceMap[sym].total += result.nativeBalance;
              balanceMap[sym].count++;
            }

            // Aggregate tokens
            for (const token of result.tokens) {
              if (token.balance > 0 && isSupportedCrypto(token.symbol)) {
                if (!balanceMap[token.symbol]) balanceMap[token.symbol] = { total: 0, count: 0 };
                balanceMap[token.symbol].total += token.balance;
                balanceMap[token.symbol].count++;
              }
            }
          } catch (e) {
            console.error(`Error checking ${network}/${address}:`, e);
          }
        }
      }

      // Build aggregated list
      const agg = Object.entries(balanceMap)
        .map(([symbol, { total, count }]) => ({
          symbol,
          totalBalance: total,
          addressCount: count,
        }))
        .sort((a, b) => b.totalBalance - a.totalBalance);

      // Group results by network
      const byNetwork: Record<string, BalanceResult[]> = {};
      for (const r of allResults) {
        if (!byNetwork[r.network]) byNetwork[r.network] = [];
        byNetwork[r.network].push(r);
      }

      setAggregated(agg);
      setNetworkDetails(byNetwork);
      setLastUpdated(new Date().toLocaleTimeString("ar-EG"));
      toast.success(`تم فحص ${allResults.length} عنوان بنجاح`);
    } catch (e) {
      console.error(e);
      toast.error("حدث خطأ أثناء جلب الأرصدة");
    } finally {
      setLoading(false);
    }
  };

  const [syncing, setSyncing] = useState(false);

  const syncBalances = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-balances");
      if (error) throw error;
      toast.success(`تمت المزامنة: ${data?.walletsUpdated || 0} محفظة تم تحديثها لـ ${data?.usersProcessed || 0} مستخدم`);
    } catch (e) {
      console.error(e);
      toast.error("حدث خطأ أثناء مزامنة الأرصدة");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card className="glass border-border col-span-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          <CardTitle className="text-sm font-medium text-muted-foreground">
            أرصدة البلوكتشين الحقيقية
          </CardTitle>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">آخر تحديث: {lastUpdated}</span>
          )}
          <Button
            size="sm"
            variant="default"
            onClick={syncBalances}
            disabled={syncing}
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowDownUp className="h-4 w-4" />
            )}
            <span className="mr-1">{syncing ? "جاري المزامنة..." : "مزامنة أرصدة المستخدمين"}</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchBalances}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="mr-1">{loading ? "جاري الفحص..." : "فحص الأرصدة"}</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 md:grid-cols-2 mb-4">
          <div className="rounded-xl border border-border bg-secondary/30 p-3">
            <p className="text-xs text-muted-foreground mb-2">رصيد العمل للمنصة</p>
            <div className="flex flex-wrap gap-2">
              {aggregated.length > 0 ? aggregated.map((b) => (
                <span key={b.symbol} className="text-xs rounded-lg bg-background/70 px-2 py-1 font-mono">
                  {b.symbol}: {b.totalBalance.toLocaleString(undefined, { maximumFractionDigits: 8 })}
                </span>
              )) : (
                <span className="text-xs text-muted-foreground">اضغط فحص الأرصدة لعرض رصيد التشغيل الحقيقي على Polygon (POS)</span>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3">
            <p className="text-xs text-muted-foreground mb-2">رصيد الأرباح</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span>أرباح جاهزة/معلقة</span><b>${(profitSummary?.pendingUsd || 0).toFixed(2)}</b></div>
              <div className="flex justify-between"><span>أرباح مرسلة للمسؤول</span><b>${(profitSummary?.sentUsd || 0).toFixed(2)}</b></div>
              <div className="flex justify-between"><span>رصيد المسؤول 2 الداخلي</span><b>{(profitSummary?.internalUsdtBalance || 0).toFixed(6)} USDT</b></div>
              <div className="font-mono text-[10px] text-muted-foreground truncate" dir="ltr">ID: {profitSummary?.recipientId || "5074064265790661"}</div>
              <div className="text-[10px] text-muted-foreground">تُرسل تلقائياً فور اعتماد كل عملية شراء إلى {profitSummary?.recipientName || "المسؤول 2"} عبر ID داخلي، وليس إلى عنوان خارجي.</div>
              {profitSummary?.recentTransfers && profitSummary.recentTransfers.length > 0 && (
                <div className="mt-2 space-y-1 border-t border-emerald-500/20 pt-2">
                  <p className="text-[10px] font-semibold text-emerald-400">آخر التحويلات المؤكدة:</p>
                  {profitSummary.recentTransfers.map((t) => (
                    <div key={t.id} className="flex justify-between text-[10px] font-mono">
                      <span className="text-primary">{t.transaction_number || "-"}</span>
                      <span>${Number(t.amount).toFixed(2)}</span>
                      <span className="text-muted-foreground">{new Date(t.created_at).toLocaleDateString("ar-EG")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {aggregated.length > 0 ? (
          <div className="space-y-4">
            {/* Aggregated totals */}
            <div className="flex flex-wrap gap-3">
              {aggregated.map((b) => (
                <div
                  key={b.symbol}
                  className="flex items-center gap-2 bg-secondary/50 rounded-lg px-3 py-2"
                >
                  <Wallet className="h-4 w-4 text-primary" />
                  <span className="font-bold text-foreground">{b.symbol}</span>
                  <span className="text-primary font-mono text-sm">
                    {b.totalBalance.toLocaleString(undefined, { maximumFractionDigits: 8 })}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({b.addressCount} عنوان)
                  </span>
                </div>
              ))}
            </div>

            {/* Network breakdown */}
            <div className="space-y-3 mt-4">
              {Object.entries(networkDetails).map(([network, results]) => {
                const nonZero = results.filter(
                  (r) => r.nativeBalance > 0 || r.tokens.some((t) => t.balance > 0)
                );
                if (nonZero.length === 0) return null;

                return (
                  <details key={network} className="group">
                    <summary className="cursor-pointer text-sm font-medium text-foreground hover:text-primary transition-colors">
                      {network} — {nonZero.length} عنوان برصيد
                    </summary>
                    <div className="mt-2 space-y-1 pr-4">
                      {nonZero.map((r) => (
                        <div
                          key={r.address}
                          className="text-xs text-muted-foreground font-mono bg-muted/30 rounded px-2 py-1 flex flex-wrap gap-2"
                        >
                          <span className="truncate max-w-[200px]">{r.address}</span>
                          {r.nativeBalance > 0 && (
                            <span className="text-foreground">
                              {r.nativeCurrency}: {r.nativeBalance.toLocaleString(undefined, { maximumFractionDigits: 8 })}
                            </span>
                          )}
                          {r.tokens
                            .filter((t) => t.balance > 0 && isSupportedCrypto(t.symbol))
                            .map((t) => (
                              <span key={t.symbol} className="text-primary">
                                {t.symbol}: {t.balance.toLocaleString(undefined, { maximumFractionDigits: 6 })}
                              </span>
                            ))}
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            {loading ? "جاري فحص العناوين على البلوكتشين..." : "اضغط 'فحص الأرصدة' لعرض الأرصدة الحقيقية من البلوكتشين"}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminBlockchainBalances;
